import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCourseAnalyticsAsync } from '../../Services/ReportService';
import { clientListAsync } from '../../Services/AdminService';
import Card from '../../Shared/Components/Card/Card';
import Header from '../../Shared/Components/Header/Header';
import Loading from '../../Shared/Loading';
import AiOverviewCard from './Components/AiOverviewCard';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';

const CourseAnalytics = () => {
    const { courseId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await clientListAsync();
                setClients(response.clients || []);
            } catch (error) {
                console.error("Error fetching clients:", error);
            }
        };

        fetchClients();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!courseId) {
                setError('No course ID provided');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await getCourseAnalyticsAsync(courseId, selectedClientId || null);
                setData(response);
            } catch (err) {
                console.error("Error fetching course analytics:", err);
                setError('Failed to load course analytics data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [courseId, selectedClientId]);

    if (loading) {
        return (
            <>
                <Header heading="Course Analytics" subheading="Loading..." icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <Loading />
                    </Card>
                </div>
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Header heading="Course Analytics" subheading="Error" icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <div className="alert alert-danger">{error || 'No data available'}</div>
                    </Card>
                </div>
            </>
        );
    }

    const { course, enrollment, completion, performance, modules, enrollmentTrend, completionTrend } = data;

    return (
        <>
            <Header
                heading={course.title}
                subheading="Course Analytics Dashboard"
                icon="stats-up"
            />
            <div className="container-fluid content">
                <AiOverviewCard aiOverview={data.aiOverview} />
                {/* Client Filter */}
                <Card>
                    <div className="row">
                        <div className="col-md-4">
                            <label className="form-label">Filter by Client</label>
                            <select
                                className="form-control"
                                value={selectedClientId}
                                onChange={(e) => setSelectedClientId(e.target.value)}
                            >
                                <option value="">All Clients</option>
                                {clients.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">Course Details</label>
                            <p className="mb-0">
                                <strong>Reference:</strong> {course.reference} |
                                <strong> Modules:</strong> {course.totalModules} |
                                <strong> Created:</strong> {moment(course.createdDate).format('DD/MM/YYYY')}
                            </p>
                            {course.description && (
                                <p className="text-muted mb-0"><small>{course.description}</small></p>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Key Metrics */}
                <div className="row">
                    <div className="col-md-3">
                        <Card title="Enrollment">
                            <div className="text-center">
                                <h2 className="mb-0">{enrollment.totalEnrollments}</h2>
                                <small className="text-muted">Total Enrollments</small>
                                <hr />
                                <div className="row text-center">
                                    <div className="col-12 mb-2">
                                        <strong>{enrollment.activeLearne}</strong><br />
                                        <small className="text-muted">Active Learners</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{enrollment.enrollmentsThisWeek}</strong><br />
                                        <small className="text-muted">Week</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{enrollment.enrollmentsThisMonth}</strong><br />
                                        <small className="text-muted">Month</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{enrollment.enrollmentsThisQuarter}</strong><br />
                                        <small className="text-muted">Quarter</small>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-3">
                        <Card title="Completion">
                            <div className="text-center">
                                <h2 className="mb-0">{completion.totalCompletions}</h2>
                                <small className="text-muted">Total Completions</small>
                                <hr />
                                <div className="row text-center">
                                    <div className="col-12 mb-2">
                                        <strong>{completion.overallCompletionRate.toFixed(1)}%</strong><br />
                                        <small className="text-muted">Completion Rate</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{completion.completionsThisWeek}</strong><br />
                                        <small className="text-muted">Week</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{completion.completionsThisMonth}</strong><br />
                                        <small className="text-muted">Month</small>
                                    </div>
                                    <div className="col-4">
                                        <strong>{completion.completionsThisQuarter}</strong><br />
                                        <small className="text-muted">Quarter</small>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-3">
                        <Card title="Performance">
                            <div className="text-center">
                                <h2 className="mb-0">{performance.averageScore.toFixed(1)}</h2>
                                <small className="text-muted">Average Score</small>
                                <hr />
                                <div className="row text-center">
                                    <div className="col-6 mb-2">
                                        <strong>{performance.medianScore.toFixed(1)}</strong><br />
                                        <small className="text-muted">Median</small>
                                    </div>
                                    <div className="col-6 mb-2">
                                        <strong>{performance.passRate.toFixed(1)}%</strong><br />
                                        <small className="text-muted">Pass Rate</small>
                                    </div>
                                    <div className="col-6">
                                        <strong className="text-success">{performance.passingLearners}</strong><br />
                                        <small className="text-muted">Passing</small>
                                    </div>
                                    <div className="col-6">
                                        <strong className="text-danger">{performance.failingLearners}</strong><br />
                                        <small className="text-muted">Failing</small>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-3">
                        <Card title="Time">
                            <div className="text-center">
                                <h2 className="mb-0">{completion.averageCompletionTime.toFixed(1)}</h2>
                                <small className="text-muted">Avg. Hours to Complete</small>
                                <hr />
                                <div className="text-center">
                                    <strong>Score Range</strong><br />
                                    <div className="mt-2">
                                        <span className="badge badge-success">High: {performance.highestScore.toFixed(1)}</span><br />
                                        <span className="badge badge-danger mt-1">Low: {performance.lowestScore.toFixed(1)}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Enrollment & Completion Trends */}
                <div className="row">
                    <div className="col-md-6">
                        <Card title="Enrollment Trend">
                            {enrollmentTrend && enrollmentTrend.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={enrollmentTrend}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="count" stroke="#8884d8" name="Enrollments" />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted">No trend data available</p>
                            )}
                        </Card>
                    </div>

                    <div className="col-md-6">
                        <Card title="Completion Trend">
                            {completionTrend && completionTrend.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={completionTrend}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="count" stroke="#82ca9d" name="Completions" />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted">No trend data available</p>
                            )}
                        </Card>
                    </div>
                </div>

                {/* Module Performance */}
                <Card title="Module Performance">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Module Title</th>
                                    <th>Type</th>
                                    <th>Attempts</th>
                                    <th>Completions</th>
                                    <th>Completion Rate</th>
                                    <th>Avg Progress</th>
                                    <th>Avg Score</th>
                                    <th>Avg Attempts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modules && modules.length > 0 ? (
                                    modules.map((module, index) => (
                                        <tr key={index}>
                                            <td>{module.ordinal}</td>
                                            <td>{module.moduleTitle}</td>
                                            <td><span className="badge badge-info">{module.moduleType}</span></td>
                                            <td>{module.totalAttempts}</td>
                                            <td>{module.completions}</td>
                                            <td>
                                                <div className="progress" style={{ height: '20px', minWidth: '100px' }}>
                                                    <div
                                                        className="progress-bar"
                                                        role="progressbar"
                                                        style={{ width: `${module.completionRate}%` }}
                                                        aria-valuenow={module.completionRate}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {module.completionRate.toFixed(0)}%
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{module.averageProgress.toFixed(1)}%</td>
                                            <td>{module.averageScore.toFixed(1)}</td>
                                            <td>{module.averageAttempts.toFixed(1)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center text-muted">No module data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {modules && modules.length > 0 && (
                        <div className="mt-4">
                            <h5>Module Completion Rates</h5>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={modules}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="moduleTitle" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="completionRate" fill="#8884d8" name="Completion Rate (%)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>
            </div>
        </>
    );
};

export default CourseAnalytics;
